import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, X, AlertTriangle } from "lucide-react";

export function DeleteCompanyDialog({ company, onClose, onDeleted }) {
  const [summary, setSummary] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    api.get(`/companies/${company.id}/delete-summary`).then(({ data }) => setSummary(data)).catch(() => toast.error("Errore nel caricamento del riepilogo"));
  }, [company.id]);
  const canDelete = confirmText.trim() === (company.name || "").trim() && !deleting;
  const doDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try { await api.delete(`/companies/${company.id}`); toast.success(`Azienda "${company.name}" eliminata definitivamente`); onDeleted(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDeleting(false); }
  };
  const rows = summary ? [
    ["Utenti", summary.counts.users], ["Prodotti", summary.counts.products], ["Fatture", summary.counts.invoices],
    ["Movimenti d'acquisto", summary.counts.purchase_movements], ["Ricette", summary.counts.recipes],
    ["Ordini fornitori", summary.counts.purchase_orders], ["Vendite", summary.counts.sales],
  ] : [];
  return (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div data-testid="delete-company-dialog" className="bg-white rounded-sm w-full max-w-lg my-8 border-t-4 border-alert" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h3 className="font-serif text-2xl text-alert flex items-center gap-2"><AlertTriangle size={22} /> Elimina azienda</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div data-testid="delete-warning-banner" className="bg-alert/10 border border-alert/30 rounded-sm p-4 text-sm text-alert flex items-start gap-3">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <span>Questa azione è <strong>irreversibile</strong>. Tutti i dati di <strong>{company.name}</strong> verranno cancellati definitivamente e non potranno essere recuperati.</span>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-2">Dati che verranno eliminati</p>
            {!summary ? <p className="text-sm text-stone-400">Caricamento riepilogo…</p> : (
              <ul data-testid="delete-summary" className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {rows.map(([label, n]) => (
                  <li key={label} className="flex justify-between border-b border-stone-100 py-1"><span className="text-stone-600">{label}</span><span className="font-semibold text-stone-900">{n}</span></li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Per confermare, digita il nome dell'azienda: <span className="text-alert normal-case font-serif">{company.name}</span></label>
            <input data-testid="delete-confirm-input" className="w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-alert/40 mt-1.5" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Nome azienda" />
          </div>
        </div>
        <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
          <button data-testid="delete-cancel-btn" onClick={onClose} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100">Annulla</button>
          <button data-testid="delete-confirm-btn" onClick={doDelete} disabled={!canDelete} className="rounded-full bg-alert text-white px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-alert/90 disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={16} /> {deleting ? "Eliminazione…" : "Elimina definitivamente"}</button>
        </div>
      </div>
    </div>
  );
}
